import React, { useEffect, useMemo, useState } from 'react';
import { Card, Button, Form, Select, InputNumber, DatePicker, Input, App, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { purchaseOrderService } from '@/services/purchaseOrder.service';
import { poMilestoneService } from '@/services/poMilestone.service';
import { serviceReceiptService } from '@/services/serviceReceipt.service';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';

const { Title } = Typography;

const CreateServiceReceiptPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [poId, setPoId] = useState<string | null>(searchParams.get('po_id'));
  const [milestoneId, setMilestoneId] = useState<string | null>(searchParams.get('milestone_id'));
  const [loading, setLoading] = useState(false);

  const { data: receivablePOs = [] } = useQuery({
    queryKey: ['purchase-orders', 'receivable', 'service_receipt'],
    queryFn: () => purchaseOrderService.list({ receivable: true, receipt_channel: 'service_receipt' }),
  });

  const milestonePOs = receivablePOs;

  const selectedPo = useMemo(
    () => milestonePOs.find((p: { id: string }) => p.id === poId) as { department_id?: string } | undefined,
    [milestonePOs, poId]
  );

  const { data: milestones = [] } = useQuery({
    queryKey: ['po-milestones', poId],
    queryFn: () => poMilestoneService.listForPo(poId!),
    enabled: !!poId,
  });

  const serviceMilestones = useMemo(
    () => milestones.filter((m) => m.fulfillment_method === 'ServiceReceipt'),
    [milestones]
  );

  const selectedMilestone = serviceMilestones.find((m) => m.id === milestoneId);

  useEffect(() => {
    if (selectedPo?.department_id) {
      form.setFieldValue('department_id', selectedPo.department_id);
    }
  }, [selectedPo?.department_id, form]);

  const handleSubmit = async () => {
    if (!poId || !milestoneId) {
      message.warning('Select PO and milestone');
      return;
    }
    try {
      const values = await form.validateFields();
      setLoading(true);
      const sr = await serviceReceiptService.create({
        po_id: poId,
        po_milestone_id: milestoneId,
        sr_date: values.sr_date ? values.sr_date.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
        service_provider_reference: values.service_provider_reference?.trim() || undefined,
        progress_percentage: values.progress_percentage,
        claimed_value: values.claimed_value ?? selectedMilestone?.milestone_amount,
        remarks: values.notes?.trim() || undefined,
        department_id: values.department_id,
      });
      message.success('Service Receipt created');
      navigate(`/scm/procurement/service-receipts/${sr.id}`);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || 'Create failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/scm/procurement/service-receipts')}>
        Back
      </Button>
      <Card title="Create Service Receipt" style={{ marginTop: 16 }}>
        <Form form={form} layout="vertical" initialValues={{ sr_date: dayjs() }}>
          <Form.Item label="Purchase Order" required>
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select milestone-based PO"
              options={milestonePOs.map((p: any) => ({
                value: p.id,
                label: `${p.po_number} — ${p.supplier_name}`,
              }))}
              value={poId ?? undefined}
              onChange={(v) => {
                setPoId(v);
                setMilestoneId(null);
              }}
            />
          </Form.Item>
          <Form.Item label="Service milestone" required>
            <Select
              placeholder="Select milestone"
              disabled={!poId}
              options={serviceMilestones.map((m) => ({
                value: m.id,
                label: `#${m.milestone_number} ${m.milestone_name} (${Number(m.milestone_amount).toFixed(2)}) — ${m.status}`,
              }))}
              value={milestoneId ?? undefined}
              onChange={setMilestoneId}
            />
          </Form.Item>
          {selectedMilestone && (
            <Typography.Paragraph type="secondary">
              Balance: {Number(selectedMilestone.balance_value ?? selectedMilestone.milestone_amount).toFixed(2)} | Status:{' '}
              {selectedMilestone.status}
            </Typography.Paragraph>
          )}
          <Form.Item
            name="department_id"
            label="Department"
            rules={[{ required: true, message: 'Department is required' }]}
          >
            <DepartmentSelect />
          </Form.Item>
          <Form.Item name="sr_date" label="SR Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="service_provider_reference" label="Service provider reference">
            <Input placeholder="Completion certificate / progress claim no." />
          </Form.Item>
          <Form.Item name="progress_percentage" label="Progress %">
            <InputNumber min={0} max={100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="claimed_value" label="Claimed value">
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              placeholder={selectedMilestone ? String(selectedMilestone.milestone_amount) : undefined}
            />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Button type="primary" onClick={handleSubmit} loading={loading} disabled={!poId || !milestoneId}>
            Create
          </Button>
        </Form>
      </Card>
    </div>
  );
};

export default CreateServiceReceiptPage;
